import chrome from './chartChrome.module.css';

/**
 * A category-axis tick that wraps instead of colliding with its neighbour.
 *
 * Only the **vertical** bar chart needs this. A rotated plot puts its categories
 * on the value axis' side, where `categoryAxisProps` gives them a fixed 224px
 * gutter and a long name has room; a vertical one shares the plot's whole width
 * between its bands, so six topics in a 586 card get about 92px each and
 * `Network coverage` wants 105. Measured on Overview (972:21115): two of its six
 * labels overlapped the label beside them.
 *
 * Wrapping rather than the two alternatives, both of which lose something a
 * reader needs:
 *
 *  - **Thinning** — what `xAxisProps` does to a dense time axis — is right for
 *    dates, where the unlabelled points are still *placed* by the labels around
 *    them. A category axis has no such order: drop `Network coverage` and the
 *    bar above it is unidentifiable.
 *  - **Truncating** to `Network cove…` costs the word that distinguishes it from
 *    `Network speed`, and does it silently.
 *
 * It is a no-op wherever the labels already fit: a name inside its band renders
 * as the single `<text>` it always did, so no chart in the app moves except the
 * ones that were overlapping. Verified by measurement across all six modules.
 *
 * Two lines at most. A third would push the axis row into the plot, so a name
 * that still does not fit in two — one long word, or more words than two lines
 * hold — is cut with an ellipsis, and the whole name stays on hover (`<title>`).
 * Live data has names like that; the corpus did not.
 */

/**
 * Average advance width of the tick face, in px per character.
 *
 * Inter at `--font-size-sm` (12px), measured across the corpus' own category
 * labels rather than taken from the font's metrics — the names are mixed-case
 * English words, so the figure that matters is their average, not `x`'s or
 * `M`'s. It only has to be close: it decides *whether* to wrap, and a label
 * within a few px of its band is fine either way.
 */
const CHAR_PX = 6.2;

/** `space/8` either side, so two neighbours never sit flush. */
const BAND_PADDING = 8;

/** Figma `_X-axis` puts the first line 16 below the axis; a second follows at 12. */
const FIRST_LINE_DY = 16;
const LINE_HEIGHT = 12;

type CategoryTickProps = {
  x?: number;
  y?: number;
  payload?: { value?: unknown };
  /** The axis' own width, which Recharts passes to a custom tick. */
  width?: number;
  visibleTicksCount?: number;
};

/** `label` cut to `budget` characters, the last one an ellipsis. */
export function fitLabel(label: string, budget: number): string {
  if (label.length <= budget) return label;
  if (budget <= 1) return '…';
  return `${label.slice(0, budget - 1).trimEnd()}…`;
}

/**
 * Split at a space so the break lands between words, never inside one, and keep
 * the halves as even as the words allow — `Customer service` reads better as
 * two balanced lines than as `Customer` over `service` would if the split were
 * simply the first space.
 */
function splitLabel(label: string, budget: number): [string] | [string, string] {
  if (label.length <= budget) return [label];

  const words = label.split(' ');
  if (words.length < 2) return [fitLabel(label, budget)];

  let best = 1;
  let bestGap = Infinity;
  for (let i = 1; i < words.length; i++) {
    const head = words.slice(0, i).join(' ').length;
    const tail = words.slice(i).join(' ').length;
    const gap = Math.abs(head - tail);
    if (gap < bestGap) {
      bestGap = gap;
      best = i;
    }
  }
  return [
    fitLabel(words.slice(0, best).join(' '), budget),
    fitLabel(words.slice(best).join(' '), budget),
  ];
}

export function CategoryTick({
  x = 0,
  y = 0,
  payload,
  width = 0,
  visibleTicksCount = 1,
}: CategoryTickProps) {
  const label = String(payload?.value ?? '');
  const band = visibleTicksCount > 0 ? width / visibleTicksCount : width;
  const budget = Math.max(1, Math.floor((band - BAND_PADDING) / CHAR_PX));
  const lines = splitLabel(label, budget);
  const cut = lines.join(' ') !== label;

  return (
    <text className={chrome.tick} x={x} y={y} textAnchor="middle">
      {cut && <title>{label}</title>}
      {lines.map((line, i) => (
        <tspan key={line} x={x} dy={i === 0 ? FIRST_LINE_DY : LINE_HEIGHT}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

type RowCategoryTickProps = {
  x?: number;
  y?: number;
  payload?: { value?: unknown };
  /** The axis' own width — the label gutter `categoryAxisProps` reserves. */
  width?: number;
};

/**
 * The category tick of a **horizontal** bar chart: left-aligned in the fixed
 * label gutter, one line, cut with an ellipsis where it would run into the
 * bars (`categoryAxisWidth` 128 holds about 19 characters). Rows are too close
 * together to wrap into. The whole name stays on hover (`<title>`).
 */
export function RowCategoryTick({ x = 0, y = 0, payload, width = 0 }: RowCategoryTickProps) {
  const label = String(payload?.value ?? '');
  const budget = Math.max(1, Math.floor((width - BAND_PADDING) / CHAR_PX));
  const line = fitLabel(label, budget);

  return (
    <text className={chrome.tick} x={x - width} y={y} dy="0.355em" textAnchor="start">
      {line !== label && <title>{label}</title>}
      {line}
    </text>
  );
}
